import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { ShieldCheck, AlertTriangle, Copy, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * WarrantCanary — ed25519-signed dead-man's-switch statement.
 *
 * Two surfaces in one file:
 *   - `<WarrantCanaryChip variant="inline" />` — compact badge
 *     suitable for the landing-page footer and Settings → Privacy
 *   - `<WarrantCanaryDialog />` — modal with the full statement,
 *     public-key fingerprint, signature, and verification steps
 *
 * State machine: `verified` (green, <24h) / `aging` (amber, 24-48h,
 * within normal refresh window) / `stale` (red, >48h — treat canary
 * as dead).
 */

const formatAge = (hours) => {
  if (hours == null) return '—';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m ago`;
  if (hours < 48) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

const statusFor = (data) => {
  if (!data || data.age_hours == null) return 'unknown';
  if (data.is_stale) return 'stale';
  if (data.age_hours >= 24) return 'aging';
  return 'verified';
};

const STATUS_COPY = {
  verified: {
    label: 'Canary verified',
    tooltip: 'Signed within the last 24h · No gag orders asserted',
    badgeClass: 'text-emerald-300 border-emerald-500/30 bg-emerald-500/10',
    dot: 'bg-emerald-400',
  },
  aging: {
    label: 'Canary aging',
    tooltip: 'Last signature is 24–48h old — still inside the refresh window',
    badgeClass: 'text-amber-300 border-amber-500/30 bg-amber-500/10',
    dot: 'bg-amber-400',
  },
  stale: {
    label: 'CANARY STALE',
    tooltip:
      'Last signature is older than 48h — the operator may have stopped signing. Treat the canary as dead.',
    badgeClass: 'text-red-300 border-red-500/40 bg-red-500/15',
    dot: 'bg-red-500 animate-pulse',
  },
  unknown: {
    label: 'Canary unreachable',
    tooltip: 'Could not fetch the canary endpoint',
    badgeClass: 'text-slate-400 border-slate-500/30 bg-slate-500/10',
    dot: 'bg-slate-500',
  },
};

const useCanary = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIt = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/canary`, { credentials: 'omit' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || 'unreachable');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIt(); }, [fetchIt]);

  return { data, loading, error, refresh: fetchIt };
};

/**
 * Inline chip for footers / settings rows. Opens the full dialog on click.
 */
export const WarrantCanaryChip = ({ className = '', showLabel = true }) => {
  const [open, setOpen] = useState(false);
  const { data, loading, error } = useCanary();
  const status = loading ? 'unknown' : error ? 'unknown' : statusFor(data);
  const copy = STATUS_COPY[status];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors hover:bg-white/5 ${copy.badgeClass} ${className}`}
        title={copy.tooltip}
        data-testid="warrant-canary-chip"
        data-canary-status={status}
      >
        <span className={`w-1.5 h-1.5 rounded-full ${copy.dot}`} />
        {showLabel && (
          <>
            <ShieldCheck className="w-3 h-3" />
            <span className="tracking-wide uppercase">{copy.label}</span>
            {!loading && data?.age_hours != null && (
              <span className="text-zinc-500 normal-case">
                · signed {formatAge(data.age_hours)}
              </span>
            )}
          </>
        )}
      </button>

      <WarrantCanaryDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

/**
 * Full statement + signature + fingerprint modal. Also exposes a
 * "Copy signature" action so auditors can re-verify OOB.
 */
export const WarrantCanaryDialog = ({ open, onClose }) => {
  const { data, loading, error, refresh } = useCanary();
  const [copiedTarget, setCopiedTarget] = useState(null);

  const status = useMemo(() => {
    if (loading) return 'unknown';
    if (error) return 'unknown';
    return statusFor(data);
  }, [data, loading, error]);
  const copy = STATUS_COPY[status];

  const copyTo = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedTarget(label);
      setTimeout(() => setCopiedTarget(null), 1500);
    } catch (_e) { /* noop */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="bg-card border-border max-w-xl" data-testid="warrant-canary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {status === 'stale' ? (
              <AlertTriangle className="w-5 h-5 text-red-400" />
            ) : (
              <ShieldCheck className="w-5 h-5 text-emerald-400" />
            )}
            Warrant canary
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            An ed25519-signed statement that the operator has not been compelled
            to hand over user data or keys. Automatically re-signed every 24h.
          </DialogDescription>
        </DialogHeader>

        {/* Status row */}
        <div className={`inline-flex items-center gap-2 self-start px-2.5 py-1 rounded-full border text-[11px] font-medium ${copy.badgeClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${copy.dot}`} />
          <span className="tracking-wide uppercase">{copy.label}</span>
          {data?.age_hours != null && (
            <span className="text-zinc-500 normal-case">
              · signed {formatAge(data.age_hours)}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Fetching canary…
          </div>
        ) : error ? (
          <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
            Could not fetch the canary endpoint: {error}. Retry, or verify the
            service is online.
          </div>
        ) : (
          <>
            {/* Hardcoded statement, rendered as-signed */}
            <ScrollArea className="max-h-48 border border-border rounded-lg bg-secondary/20">
              <pre
                className="text-[11.5px] leading-relaxed text-slate-200 whitespace-pre-wrap p-3 font-mono"
                data-testid="warrant-canary-statement"
              >
                {data.statement}
              </pre>
            </ScrollArea>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="rounded-lg border border-border p-2.5 space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">
                  Signed at
                </p>
                <p className="font-mono text-slate-200" data-testid="warrant-canary-signed-at">
                  {data.signed_at
                    ? new Date(data.signed_at).toLocaleString()
                    : '—'}
                </p>
                <p className="text-[10px] text-slate-500">
                  {formatAge(data.age_hours)} · re-signs every 24h
                </p>
              </div>
              <div className="rounded-lg border border-border p-2.5 space-y-1">
                <p className="text-[10px] uppercase tracking-wide text-slate-500">
                  Public key fingerprint
                </p>
                <p
                  className="font-mono text-[11px] text-slate-200 tracking-[0.2em] break-all"
                  data-testid="warrant-canary-fingerprint"
                  title={data.public_key_hex}
                >
                  {data.public_key_fingerprint_short}
                </p>
                <p className="text-[10px] text-slate-500">
                  Verify this out-of-band before trusting the signature.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                onClick={() => copyTo(data.signature_b64, 'sig')}
                data-testid="warrant-canary-copy-sig"
              >
                <Copy className="w-3.5 h-3.5" />
                {copiedTarget === 'sig' ? 'Copied' : 'Copy signature'}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => copyTo(data.public_key_hex, 'pub')}
                data-testid="warrant-canary-copy-pub"
              >
                <Copy className="w-3.5 h-3.5" />
                {copiedTarget === 'pub' ? 'Copied' : 'Copy public key'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={refresh}
                className="text-slate-400 hover:text-white ml-auto"
                data-testid="warrant-canary-refresh"
              >
                Refresh
              </Button>
            </div>

            {status === 'stale' && (
              <div
                className="text-[11.5px] text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg p-3"
                data-testid="warrant-canary-stale-warning"
              >
                <strong>The canary is stale.</strong> The last signature is
                older than {data.stale_threshold_hours}h. One or more of the
                assertions above may no longer be true — the operator has
                either stopped signing, lost control of the deployment, or
                been compelled to stop. Treat this as a dead-man's signal.
              </div>
            )}

            {/* Cross-verify CTA. Opens in a new tab so the dialog
                state isn't lost — the user can diff this card's
                signature against the Matrix-mirrored copies without
                losing their place. */}
            <div className="pt-3 border-t border-border/40 text-[11.5px] text-slate-400 leading-relaxed">
              Don't trust this server alone.{' '}
              <a
                href="/transparency"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline decoration-primary/40 underline-offset-2"
                data-testid="warrant-canary-cross-verify"
              >
                Verify publicly →
              </a>{' '}
              cross-checks this signature against every Matrix room the
              bridge is mirroring to.
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WarrantCanaryChip;
